import {
  useForm,
  SubmitHandler,
  FieldValues,
} from 'react-hook-form';
import { Modal, Button, Form } from 'react-bootstrap';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentEmailForm } from '../types/user.types';

interface EditEmailModalProps {
  show: boolean;
  currentEmail: string;
  saveUserInfo: (_result: boolean, _data: any) => void;
  emailExist: boolean;
  setEmailExist: (_exist: boolean) => void;
}

function EditEmailModal({
  show,
  saveUserInfo,
  currentEmail,
  emailExist,
  setEmailExist,
}: EditEmailModalProps): JSX.Element {
  const { t } = useTranslation();
  const submitRef = useRef<HTMLButtonElement>(null);
  const [showWarning, setShowWarning] = useState(false);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<CurrentEmailForm>();

  const onSubmit: SubmitHandler<FieldValues> = (data: FieldValues) => {
    if (data.newEmail === data.updatedEmail) {
      saveUserInfo(true, {
        email: data.newEmail,
        type: 'email',
      });
      setShowWarning(false);
      setEmailExist(false);
      reset();
    } else {
      setEmailExist(false);
      setShowWarning(true);
    }
  };

  function handleClose(): void {
    setShowWarning(false);
    saveUserInfo(false, {});
    setEmailExist(false);
    reset();
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      className="modal"
      backdrop="static"
      size="lg"
    >
      <Modal.Header className="text-center" closeButton>
        <Modal.Title>{t('Update_Email')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showWarning && (
          <div className="col-lg-8 col-12 mx-auto text-center error-text">
            {t('Email_Update_Warning')}
          </div>
        )}
        {emailExist && (
          <div className="col-lg-8 col-12 mx-auto text-center error-text">
            {t('Email_Exists')}
          </div>
        )}
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group>
            <Form.Label>{t('Current_Email')}</Form.Label>
            <Form.Control
              value={currentEmail}
              readOnly
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="mt-3 required">{t('New_Email')}</Form.Label>
            <Form.Control
              type="email"
              {...register('newEmail', { required: true })}
              isInvalid={!!errors.newEmail}
            />
            {errors.newEmail && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label className="mt-3 required">{t('Confirm_Email')}</Form.Label>
            <Form.Control
              type="email"
              {...register('updatedEmail', { required: true })}
              isInvalid={!!errors.updatedEmail}
            />
            {errors.updatedEmail && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <button
            ref={submitRef}
            type="submit"
            style={{ display: 'none' }}
            aria-label="submit"
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={handleClose}>
          {t('PDP.Cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="col-3"
          onClick={() => { submitRef.current?.click(); }}
        >
          {t('PDP.Save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditEmailModal;
